/**
 * Tenant detail — the full page behind a row in the tenants list.
 *
 * Replaces the old edit modal. Per-tenant actions live here rather than as row
 * buttons, so the list is a list and this page is where a tenant is inspected
 * and changed.
 *
 * **Three tabs, in the order a question about a tenant is asked**: what it is,
 * who runs it, who teaches in it. They were stacked cards, which put the teaching
 * staff below a long branding form and left "who administers this?" as a row of
 * raw ids at the very bottom.
 *
 * The administrators tab is the existing `TenantAdmins` page, embedded and locked
 * to this tenant, rather than a second listing — that page already reads the
 * authority (`roleAssignments`), and a copy here would drift from it.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    BackButton, Card, CardHeader, CardContent, TabStrip,
    Button, Badge, FormField, Input, Select, formatDateShort,
} from '@shared-ui/components';
import { ImageUploadField } from '@shared-ui/question-edit-form';
import { tenantsApi, type Tenant, type TenantStatus, type UpdateTenantRequest } from '../../../api/tenantsApi';
import { PLATFORM_TENANT_ID, TENANT_STATUSES, isReserved, statusVariant } from './tenantHelpers';
import { HardDeleteTenantModal } from './HardDeleteTenantModal';
import { TeachingStaffCard } from './TeachingStaffCard';
import { TenantGatesCard } from './TenantGatesCard';
import { TenantSettingsTab } from './TenantSettingsTab';
import TenantAdmins from '../../admin/tenant-admins/TenantAdmins';

const TABS = [
    { key: 'general', label: 'General' },
    { key: 'admins', label: 'Tenant admins' },
    { key: 'staff', label: 'Teaching staff' },
    { key: 'settings', label: 'Settings' },
    { key: 'gates', label: 'Feature gates' },
] as const;
type TabKey = typeof TABS[number]['key'];
import './Tenants.css';

export default function TenantDetail() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [error, setError] = useState('');
    const [hardDeleting, setHardDeleting] = useState<Tenant | null>(null);
    const [tab, setTab] = useState<TabKey>('general');

    const { data: tenant, isLoading, error: loadError } = useQuery({
        queryKey: ['tenant', id],
        queryFn: () => tenantsApi.getById(id),
        enabled: !!id,
    });

    // Parent options come from the full list — a tenant can be reparented onto
    // any other tenant, so the picker needs them all (minus itself).
    const { data: listData } = useQuery({ queryKey: ['tenants'], queryFn: () => tenantsApi.list() });
    const parentOptions = (listData?.tenants ?? []).filter(t => t.id !== id).map(t => t.id);

    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [status, setStatus] = useState<TenantStatus>('active');
    const [parentTenantId, setParentTenantId] = useState('');
    const [logoUrl, setLogoUrl] = useState<string | null>(null);

    // Seed the form once the tenant loads, and re-seed after a save so the
    // baseline the dirty check compares against is the saved doc.
    useEffect(() => {
        if (!tenant) return;
        setName(tenant.name);
        setSlug(tenant.slug ?? tenant.id);
        setStatus(tenant.status);
        setParentTenantId(tenant.parentTenantId ?? '');
        setLogoUrl(tenant.branding?.logoUrl ?? null);
    }, [tenant]);

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['tenant', id] });
        queryClient.invalidateQueries({ queryKey: ['tenants'] });
    };

    const updateMutation = useMutation({
        mutationFn: (body: UpdateTenantRequest) => tenantsApi.update(id, body),
        onSuccess: () => { invalidate(); setError(''); },
        onError: (err: Error) => setError(err.message),
    });

    const archiveMutation = useMutation({
        mutationFn: () => tenantsApi.archive(id),
        onSuccess: () => { invalidate(); setError(''); },
        onError: (err: Error) => setError(err.message),
    });

    if (isLoading) return <div className="tenants-page"><BackButton label="tenants" to="/tenants" /><p>Loading…</p></div>;
    if (loadError || !tenant) {
        return (
            <div className="tenants-page">
                <BackButton label="tenants" to="/tenants" />
                <div className="tenants-error">
                    {loadError instanceof Error ? loadError.message : `Tenant "${id}" not found`}
                </div>
            </div>
        );
    }

    const reserved = isReserved(tenant.id);

    const changes = (): UpdateTenantRequest => {
        const body: UpdateTenantRequest = {};
        if (name !== tenant.name) body.name = name;
        if (!reserved) {
            // Compare against the effective slug, so a pre-backfill doc (slug
            // absent, falling back to id) is not reported dirty on load.
            if (slug !== (tenant.slug ?? tenant.id)) body.slug = slug;
            if (status !== tenant.status) body.status = status;
            const newParent = parentTenantId || null;
            const oldParent = tenant.parentTenantId ?? null;
            if (newParent !== oldParent) body.parentTenantId = newParent;
        }
        if ((logoUrl ?? '') !== (tenant.branding?.logoUrl ?? '')) {
            body.branding = { ...tenant.branding, logoUrl: logoUrl ?? undefined };
        }
        return body;
    };
    const dirty = Object.keys(changes()).length > 0;

    function handleSave(e?: React.FormEvent) {
        e?.preventDefault();
        const body = changes();
        if (Object.keys(body).length === 0) return;
        updateMutation.mutate(body);
    }

    return (
        <div className="tenants-page">
            <BackButton label="tenants" to="/tenants" />

            <header className="tenant-detail-header">
                <div className="tenant-detail-title">
                    <h1>{tenant.name}</h1>
                    <code className="tenants-id">{tenant.id}</code>
                    <Badge variant={statusVariant(tenant.status)}>{tenant.status}</Badge>
                </div>
                {/* Destructive actions live with the tenant, not on a list row —
                    archiving from a row made it easy to hit the wrong one. */}
                {!reserved && (
                    <div className="tenants-actions">
                        {tenant.status !== 'archived' && (
                            <Button
                                variant="danger"
                                onClick={() => {
                                    if (confirm(`Archive tenant "${tenant.id}"?`)) archiveMutation.mutate();
                                }}
                                loading={archiveMutation.isPending}
                            >
                                Archive
                            </Button>
                        )}
                        {tenant.status === 'archived' && (
                            <Button variant="danger" onClick={() => { setHardDeleting(tenant); setError(''); }}>
                                Hard delete
                            </Button>
                        )}
                    </div>
                )}
            </header>

            {error && <div className="tenants-error">{error}</div>}
            {reserved && (
                <div className="tenants-info">
                    Reserved tenant. Status and parent are locked, and it cannot be archived or deleted.
                </div>
            )}

            <TabStrip
                ariaLabel="Tenant views"
                active={tab}
                onChange={t => setTab(t as TabKey)}
                // The platform tenant has no members, so nothing for a gate override to reach.
                tabs={TABS
                    .filter(t => !(t.key === 'gates' && tenant.id === PLATFORM_TENANT_ID))
                    .map(t => ({
                        key: t.key,
                        label: t.key === 'settings' && tenant.id === PLATFORM_TENANT_ID ? 'Platform defaults' : t.label,
                    }))}
            />

            {tab === 'admins' && <TenantAdmins tenantId={tenant.id} embedded />}
            {tab === 'staff' && <TeachingStaffCard tenantId={tenant.id} />}
            {tab === 'settings' && <TenantSettingsTab tenantId={tenant.id} />}
            {tab === 'gates' && <TenantGatesCard tenantId={tenant.id} />}

            {tab === 'general' && (<>
            <Card padding="none">
                <CardHeader title="Details" subtitle="Identity, placement in the tenant tree, and branding" />
                <CardContent>
                    <form onSubmit={handleSave} className="tenants-form">
                        <FormField label="ID">
                            <Input type="text" value={tenant.id} disabled />
                        </FormField>

                        <FormField label="Type">
                            <Input type="text" value={tenant.type} disabled />
                        </FormField>

                        <FormField label="Name" required>
                            <Input type="text" value={name} onChange={e => setName(e.target.value)} />
                        </FormField>

                        {!reserved && (
                            <FormField label="URL slug">
                                <Input type="text" value={slug} onChange={e => setSlug(e.target.value.toLowerCase())} />
                                <small>
                                    The tenant's URL label — <code>{slug || 'slug'}.buzzbrain.ai</code>,{' '}
                                    <code>{slug || 'slug'}-teacher.buzzbrain.ai</code>,{' '}
                                    <code>buzzbrain.ai/t/{slug || 'slug'}</code>. Renaming keeps the old
                                    slug working as a redirect, but a provisioned host needs a new
                                    Cloudflare domain and Google OAuth origin before the old one is
                                    detached. May not end in <code>-student</code>/<code>-teacher</code>/
                                    <code>-admin</code>.
                                </small>
                                {tenant.previousSlugs && tenant.previousSlugs.length > 0 && (
                                    <small>Previously: {tenant.previousSlugs.join(', ')}</small>
                                )}
                            </FormField>
                        )}

                        <FormField label="Status">
                            <Select
                                value={status}
                                onChange={e => setStatus(e.target.value as TenantStatus)}
                                disabled={reserved}
                            >
                                {TENANT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </Select>
                        </FormField>

                        <FormField label="Parent tenant">
                            <Select
                                value={parentTenantId}
                                onChange={e => setParentTenantId(e.target.value)}
                                disabled={reserved}
                            >
                                <option value="">— top-level —</option>
                                {parentOptions.map(p => <option key={p} value={p}>{p}</option>)}
                            </Select>
                        </FormField>

                        <ImageUploadField
                            label="Logo (shown in the app sidebar; default used if none)"
                            value={logoUrl}
                            onChange={setLogoUrl}
                            maxPreviewHeight={60}
                            onUploadImage={async (base64, mimeType) => {
                                const { url } = await tenantsApi.uploadLogo(tenant.id, base64, mimeType);
                                return { url };
                            }}
                        />

                        <div className="tenant-detail-save">
                            {dirty
                                ? <span className="tenant-detail-note">Unsaved changes</span>
                                : (updateMutation.isSuccess && !updateMutation.isPending && (
                                    <span className="tenant-detail-note is-saved">Saved</span>
                                ))}
                            <Button
                                onClick={handleSave}
                                loading={updateMutation.isPending}
                                disabled={!dirty || !name.trim()}
                            >
                                Save
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            <Card padding="none">
                <CardHeader title="Record" />
                <CardContent>
                    <dl className="tenant-detail-meta">
                        <dt>Created</dt>
                        <dd>{formatDateShort(tenant.createdAt)} by {tenant.createdBy}</dd>
                        {tenant.updatedAt && (
                            <>
                                <dt>Last updated</dt>
                                <dd>{formatDateShort(tenant.updatedAt)}{tenant.updatedBy ? ` by ${tenant.updatedBy}` : ''}</dd>
                            </>
                        )}
                    </dl>
                    {/* "Tenant admins" used to be a row here, rendering the tenant's
                        `adminUserIds` array. That array is written only when someone
                        edits the tenant through this page, so a school provisioned from
                        its own staff list showed "—" while an account with tenant_admin
                        for it was signing in and administering it. The tab reads the
                        assignments, which are the authority. */}
                </CardContent>
            </Card>
            </>)}

            <HardDeleteTenantModal
                tenant={hardDeleting}
                onClose={() => setHardDeleting(null)}
                onDeleted={() => {
                    setHardDeleting(null);
                    queryClient.invalidateQueries({ queryKey: ['tenants'] });
                    // The tenant this page is about no longer exists.
                    navigate('/tenants');
                }}
            />
        </div>
    );
}
